import { UrlFilters } from '@elementary/common-ui/hooks/useUrlFilters';
import theme from '@elementary/common-ui/style/theme';
import { TestResult, Totals, Report } from '@elementary/common-ui/types';
import {
  distinctArrayBy,
  groupBy,
  sumArr,
  intersectArrays,
  debugLog,
} from '@elementary/common-ui/utils/common';
import {
  BasicEdge,
  Graph,
  GraphUtil,
  MutableUnweightedGraph,
} from 'graphs-for-js';
import type { Theme } from '@mui/material';
import {
  Node as ReactFlowNode,
  Edge as ReactFlowEdge,
  ReactFlowState,
} from 'reactflow';
import { PlatformType } from '../types';
import {
  GRAPH_NODE_HEIGHT,
  GRAPH_NODE_HORIZONTAL_SPACING,
  GRAPH_NODE_VERTICAL_SPACING,
  GRAPH_NODE_MIN_WIDTH,
  GRAPH_NODE_MAX_WIDTH,
  GRAPH_PARENT_NODE_HEIGHT,
  GRAPH_CHILD_NODE_HEIGHT,
  GRAPH_CHILD_NODE_VERTICAL_SPACING,
  GRAPH_NODE_SEARCH_AREA_HEIGHT,
  CHILD_NODE_PADDING,
  NODE_Z_INDEX,
  EXPANDED_NODE_Z_INDEX,
  MAX_GRAPH_NODES_WARNING,
  MAX_GRAPH_EDGES_WARNING,
  LINEAGE_PILL_ZOOM_THRESHOLD,
  LINEAGE_DOT_ZOOM_THRESHOLD,
  LINEAGE_DOT_MIN_DIAMETER,
  LINEAGE_DOT_MAX_DIAMETER,
  LINEAGE_PILL_BOTTOM,
} from '../constants';
import {
  EnrichedLineageNode,
  GraphNodeBorderColorParams,
  HopLimit,
  LineageNodeDegrees,
  LineageZoomTier,
  LogicalEdge,
  ReactFlowGraph,
  ReactFlowNodeData,
  StreamDirection,
  WorkerData,
  ApiLineage,
  ApiLineageNode,
} from '../types';
import { isColumnNode } from './lineageApiParsing';

const edgeStyle = {
  default: { stroke: theme.palette.icon.disabled },
  parent: { stroke: theme.palette.icon.disabled, strokeWidth: 2 },
  child: {
    stroke: theme.palette.icon.disabled,
    strokeDasharray: 4,
    strokeWidth: 2.5,
  },
  greyedOut: { stroke: theme.tokens.components.lineage.greyedOutStroke },
  highlighted: { stroke: theme.palette.accent.secondary },
};

const dedupeReactFlowEdges = (edges: ReactFlowEdge[]) => {
  const edgesById = new Map<string, ReactFlowEdge>();

  edges.forEach((edge) => {
    const existing = edgesById.get(edge.id);
    if (!existing) {
      edgesById.set(edge.id, edge);
      return;
    }

    const existingLogicalEdges =
      existing.data?.logicalEdges ??
      (existing.source && existing.target
        ? [{ source: existing.source, target: existing.target }]
        : []);
    const nextLogicalEdges =
      edge.data?.logicalEdges ??
      (edge.source && edge.target
        ? [{ source: edge.source, target: edge.target }]
        : []);
    const mergedLogicalEdges = Array.from(
      new Map(
        [...existingLogicalEdges, ...nextLogicalEdges].map((logicalEdge) => [
          `${logicalEdge.source}->${logicalEdge.target}`,
          logicalEdge,
        ]),
      ).values(),
    );

    edgesById.set(edge.id, {
      ...existing,
      hidden: Boolean(existing.hidden) && Boolean(edge.hidden),
      zIndex: Math.max(existing.zIndex || 0, edge.zIndex || 0),
      style: edge.hidden ? existing.style : edge.style,
      data: {
        ...(existing.data || {}),
        ...(edge.data || {}),
        logicalEdges: mergedLogicalEdges,
      },
    });
  });

  return [...edgesById.values()];
};

const getRenderedEdgeNodeId = (node: EnrichedLineageNode) =>
  node.isRestricted && node.parent ? node.parent : node.id;

export const buildRenderedEdgesFromFilteredGraph = (
  filteredGraph: MutableUnweightedGraph<EnrichedLineageNode, string>,
) => {
  const renderedEdgeMap = new Map<
    string,
    {
      source: string;
      target: string;
      style: object;
      logicalEdges: LogicalEdge[];
    }
  >();

  filteredGraph.edges().forEach((edge) => {
    const source = getRenderedEdgeNodeId(edge.source);
    const target = getRenderedEdgeNodeId(edge.target);

    if (source === target) {
      return;
    }

    const key = `${source}-${target}`;
    const logicalEdge = { source: edge.source.id, target: edge.target.id };
    const isChildEdge = !!edge.source.parent || !!edge.target.parent;

    const existing = renderedEdgeMap.get(key);
    if (existing) {
      existing.logicalEdges.push(logicalEdge);
      return;
    }

    renderedEdgeMap.set(key, {
      source,
      target,
      style: isChildEdge ? edgeStyle.child : edgeStyle.parent,
      logicalEdges: [logicalEdge],
    });
  });

  return [...renderedEdgeMap.entries()].map(([id, edge]) => ({
    id,
    source: edge.source,
    target: edge.target,
    style: edge.style,
    data: { logicalEdges: edge.logicalEdges },
  }));
};

export const initGraph = (workerData: WorkerData) => {
  const newGraph = new Graph<EnrichedLineageNode, string>()
    .keyFn((n) => n.id)
    .directed.unweighted();

  if (!workerData.enrichedLineageData) return newGraph;

  const { enrichedLineageData } = workerData;

  enrichedLineageData.nodes.forEach((element) => {
    newGraph.insert(element);
  });

  const lineageDataMap: Record<string, EnrichedLineageNode> = {};

  enrichedLineageData.nodes.forEach((n) => {
    lineageDataMap[n.id] = n;
  });

  workerData.enrichedLineageData.edges.forEach((e: string[]) => {
    const source = lineageDataMap[e[1]];
    const target = lineageDataMap[e[0]];

    if (source && target && source.id !== target.id) {
      newGraph.connect(source, target);
    }
  });

  return newGraph;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const findPath = (
  workerData: WorkerData,
  source: EnrichedLineageNode,
  target: EnrichedLineageNode,
  searchGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
): boolean => {
  if (!workerData.graph) return false;

  const queue: EnrichedLineageNode[] = [];
  const visited: Record<string, boolean> = {};

  queue.push(source);

  visited[source.id] = true;

  while (queue.length > 0) {
    const curNode = queue.shift();

    if (!curNode) break;

    if (curNode.id === target.id) {
      return true;
    }

    const outgoingNodes = (searchGraph || workerData.graph)
      .outgoingEdgesOf(curNode)
      ?.map((e) => e.target);

    for (let i = 0; i < outgoingNodes.length; i += 1) {
      const neighbour = outgoingNodes[i];

      if (!visited[neighbour.id]) {
        queue.push(neighbour);
        visited[neighbour.id] = true;
      }
    }
  }
  return false;
};

const getStreamDirectionNodes = (
  workerData: WorkerData,
  roots: EnrichedLineageNode[],
  direction: StreamDirection,
  searchGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
  depthLimit?: number,
) => {
  if (!workerData.graph) return [];

  const queue: Array<[EnrichedLineageNode, number]> = [];
  const visited: Record<string, boolean> = {};
  const result: EnrichedLineageNode[] = [];
  const nodeDepth: Record<string, number> = {};

  roots.forEach((root) => {
    queue.push([root, 0]);
    visited[root.id] = true;
    nodeDepth[root.id] = 0;
  });

  roots.forEach((root) => {
    visited[root.id] = true;
  });

  while (queue.length > 0) {
    const [curNode, curDepth] = queue.shift() as [EnrichedLineageNode, number];

    if (!curNode) break;

    if (depthLimit === undefined || curDepth <= depthLimit) {
      const nodes =
        direction === 'upstream'
          ? (searchGraph || workerData.graph)
              .incomingEdgesOf(curNode)
              ?.map((e) => e.source)
          : (searchGraph || workerData.graph)
              .outgoingEdgesOf(curNode)
              ?.map((e) => e.target);

      for (let i = 0; i < nodes.length; i += 1) {
        const neighbour = nodes[i];

        if (!visited[neighbour.id]) {
          queue.push([neighbour, curDepth + 1]);
          visited[neighbour.id] = true;
          nodeDepth[neighbour.id] = curDepth + 1;
          result.push(neighbour);
        }
      }
    }
  }

  return result.filter(
    (node) => depthLimit === undefined || nodeDepth[node.id] <= depthLimit,
  );
};

const getDagMaxDepth = (
  workerData: WorkerData,
  roots: EnrichedLineageNode[],
  direction: StreamDirection,
  searchGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
) => {
  if (!workerData.graph) return 0;

  const queue: Array<[EnrichedLineageNode, number]> = [];
  const visited: Record<string, boolean> = {};
  const nodeDepth: Record<string, number> = {};

  roots.forEach((root) => {
    queue.push([root, 0]);
    visited[root.id] = true;
    nodeDepth[root.id] = 0;
  });

  while (queue.length > 0) {
    const [curNode, curDepth] = queue.shift() as [EnrichedLineageNode, number];

    if (!curNode) break;

    const nodes =
      direction === 'upstream'
        ? (searchGraph || workerData.graph)
            .incomingEdgesOf(curNode)
            ?.map((e) => e.source)
        : (searchGraph || workerData.graph)
            .outgoingEdgesOf(curNode)
            ?.map((e) => e.target);

    for (let i = 0; i < nodes.length; i += 1) {
      const neighbour = nodes[i];

      if (!visited[neighbour.id]) {
        queue.push([neighbour, curDepth + 1]);
        visited[neighbour.id] = true;
        nodeDepth[neighbour.id] = curDepth + 1;
      }
    }
  }
  return Object.keys(nodeDepth).length === 0
    ? 0
    : Math.max(...Object.values(nodeDepth));
};

export const getNodesOnPath = (
  workerData: WorkerData,
  roots: EnrichedLineageNode[],
  searchGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
  includeRoots?: boolean,
) => {
  const incoming = getStreamDirectionNodes(
    workerData,
    roots,
    'upstream',
    searchGraph,
  );
  const outgoing = getStreamDirectionNodes(
    workerData,
    roots,
    'downstream',
    searchGraph,
  );
  return includeRoots
    ? [...roots, ...incoming, ...outgoing]
    : [...incoming, ...outgoing];
};

export const getDagDepthBoundary = (
  workerData: WorkerData,
  roots: EnrichedLineageNode[],
  searchGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
) => {
  const incomingDepth = getDagMaxDepth(
    workerData,
    roots,
    'upstream',
    searchGraph,
  );
  const outgoingDepth = getDagMaxDepth(
    workerData,
    roots,
    'downstream',
    searchGraph,
  );
  return { incomingDepth, outgoingDepth };
};

const getOutgoingEdges = (
  workerData: WorkerData,
  roots: EnrichedLineageNode[],
  searchGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
) => {
  const { graph } = workerData;
  if (!graph) return [];

  const queue: EnrichedLineageNode[] = [];
  const visited: Record<string, boolean> = {};
  const result: BasicEdge<EnrichedLineageNode, string>[] = [];

  queue.push(...roots);

  while (queue.length > 0) {
    const curNode = queue.shift();

    if (!curNode) break;

    const outgoingEdges = (searchGraph || graph).outgoingEdgesOf(curNode);
    const outgoingNodes = outgoingEdges.map((e) => e.target);

    result.push(...outgoingEdges);

    for (let i = 0; i < outgoingNodes.length; i += 1) {
      const node = outgoingNodes[i];

      if (!visited[node.id]) {
        queue.push(node);
        visited[node.id] = true;
      }
    }
  }
  return result;
};

const getIncomingEdges = (
  workerData: WorkerData,
  roots: EnrichedLineageNode[],
  searchGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
) => {
  const { graph } = workerData;
  if (!graph) return [];

  const queue: EnrichedLineageNode[] = [];
  const visited: Record<string, boolean> = {};
  const result: BasicEdge<EnrichedLineageNode, string>[] = [];

  queue.push(...roots);

  while (queue.length > 0) {
    const curNode = queue.shift();

    if (!curNode) break;

    const incomingEdges = (searchGraph || graph).incomingEdgesOf(curNode);
    const incomingNodes = incomingEdges.map((e) => e.source);

    result.push(...incomingEdges);

    for (let i = 0; i < incomingNodes.length; i += 1) {
      const node = incomingNodes[i];

      if (!visited[node.id]) {
        queue.push(node);
        visited[node.id] = true;
      }
    }
  }
  return result;
};

const getEdgesOnPath = (
  workerData: WorkerData,
  roots: EnrichedLineageNode[],
  searchGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
) => {
  const incoming = getIncomingEdges(workerData, roots, searchGraph);
  const outgoing = getOutgoingEdges(workerData, roots, searchGraph);
  return [...incoming, ...outgoing];
};

const nearestMultiple = (n: number, mul: number) => Math.round(n / mul) * mul;

// Renders the children-section header when it has loaded children or a
// non-zero columnsCount. Must mirror GraphNode.tsx to avoid layout overlaps.
const getReactFlowNodeCollapsedHeight = (
  node: ReactFlowNode<ReactFlowNodeData>,
) => {
  const hasChildrenSection =
    !!node.data.children ||
    (node.data.columnsCount ?? 0) > 0 ||
    (node.data.metadata.columnsCount ?? 0) > 0;
  return hasChildrenSection ? GRAPH_PARENT_NODE_HEIGHT : GRAPH_NODE_HEIGHT;
};

// Calculates a node Y coordinate to be closest to it's ancestors in the graph.
const getNodeYCoor = (
  node: EnrichedLineageNode,
  subGraph: MutableUnweightedGraph<EnrichedLineageNode, string>,
  layoutedNodes: ReactFlowNode<ReactFlowNodeData>[],
  layoutedDepthNodes: ReactFlowNode<ReactFlowNodeData>[],
  depth: number,
  nodeIndexInDepth: number,
) => {
  // When the depth is 1, simply position the nodes in a vertical column,
  // since the nodes doesnt have ancestors to effect their Y coor.
  if (depth === 1) {
    let minYValue = 0;

    // Calculate the min Y value of the node, By iterating through previous nodes
    // in the depth array, and summing up their heights
    for (let i = 0; i < nodeIndexInDepth; i += 1) {
      const collapsedNodeHeight = getReactFlowNodeCollapsedHeight(
        layoutedDepthNodes[i],
      );

      minYValue += collapsedNodeHeight + GRAPH_NODE_VERTICAL_SPACING;
    }

    return nearestMultiple(
      minYValue,
      GRAPH_NODE_HEIGHT + GRAPH_NODE_VERTICAL_SPACING,
    );
  }

  const nodeAncestors: EnrichedLineageNode[] = subGraph
    .incomingEdgesOf(node)
    .map((edge) => edge.source);

  const ancestorsYCoors = nodeAncestors
    .map(
      (anc) =>
        layoutedNodes.find((layoutedNode) => layoutedNode.id === anc.id)
          ?.position.y,
    )
    .filter((n) => n != null) as number[];

  if (ancestorsYCoors.length === 0) {
    let minYValue = 0;
    for (let i = 0; i < nodeIndexInDepth; i += 1) {
      const collapsedNodeHeight = getReactFlowNodeCollapsedHeight(
        layoutedDepthNodes[i],
      );
      minYValue += collapsedNodeHeight + GRAPH_NODE_VERTICAL_SPACING;
    }
    return nearestMultiple(
      minYValue,
      GRAPH_NODE_HEIGHT + GRAPH_NODE_VERTICAL_SPACING,
    );
  }

  const averageParentsYCoor =
    ancestorsYCoors.reduce((a: number, b: number) => a + b, 0) /
    ancestorsYCoors.length;

  // Position the node so it's middle point (height wise)
  // will be directly aligned with the average of it's ancestors middle points.
  return nearestMultiple(
    averageParentsYCoor,
    GRAPH_NODE_HEIGHT + GRAPH_NODE_VERTICAL_SPACING,
  );
};

// Prevents nodes from overlapping each other
const alignOverlappingNodes = (
  layoutedDepthNodes: ReactFlowNode<ReactFlowNodeData>[],
  lockPosNodes?: string[],
) => {
  const lockedPos = lockPosNodes
    ? layoutedDepthNodes.filter((n) => lockPosNodes?.includes(n.id))
    : [];
  const nonLockedPos = lockPosNodes
    ? layoutedDepthNodes.filter((n) => !lockPosNodes?.includes(n.id))
    : layoutedDepthNodes;
  const sortedNodes = nonLockedPos.sort((a, b) => a.position.y - b.position.y);
  const nodeArr = [...lockedPos, ...sortedNodes];

  const positionedNodes: ReactFlowNode<ReactFlowNodeData>[] = [];

  nodeArr.forEach((node) => {
    let uniqueYCoor = node.position.y;
    const isLockedPos = lockPosNodes?.includes(node.id);

    while (
      !isLockedPos &&
      positionedNodes.find((layoutedNode) => {
        const nodeCollapsedHeight =
          getReactFlowNodeCollapsedHeight(layoutedNode);

        const nodeHeight = layoutedNode.data.isExpanded
          ? layoutedNode.data.height
          : nodeCollapsedHeight;

        return (
          (uniqueYCoor >= layoutedNode.position.y &&
            uniqueYCoor <
              layoutedNode.position.y +
                nodeHeight +
                GRAPH_NODE_VERTICAL_SPACING) ||
          (uniqueYCoor <= layoutedNode.position.y &&
            uniqueYCoor + nodeHeight >= layoutedNode.position.y)
        );
      })
    ) {
      uniqueYCoor += GRAPH_NODE_VERTICAL_SPACING;
    }

    positionedNodes.push({
      ...node,
      position: {
        ...node.position,
        y: isLockedPos ? uniqueYCoor : uniqueYCoor < 0 ? 0 : uniqueYCoor,
      },
    });
  });

  return positionedNodes;
};

const isGradParentNode = (node: EnrichedLineageNode) => {
  const isSigmaPage =
    node.subType === 'page' &&
    node.platform?.toLowerCase() === PlatformType.Sigma;

  const isThoughtSpotLiveboard =
    node.subType === 'liveboard' &&
    node.platform?.toLowerCase() === PlatformType.ThoughtSpot;

  const isHexProject =
    node.subType === 'project' &&
    node.platform?.toLowerCase() === PlatformType.Hex;

  const isLightdashDashboard =
    node.subType === 'dashboard' &&
    node.platform?.toLowerCase() === PlatformType.Lightdash;

  const isSemanticView = node.type === 'semantic_view' && !node.parent;

  return (
    isSigmaPage ||
    isThoughtSpotLiveboard ||
    isHexProject ||
    isLightdashDashboard ||
    isSemanticView
  );
};

export const isNodeColumnHighlightFiltered = (
  node: EnrichedLineageNode,
  highlightColumnIds?: Set<string>,
  unfilteredColumnParentId?: string,
): boolean => {
  if (!highlightColumnIds) return false;
  if (unfilteredColumnParentId && node.id === unfilteredColumnParentId) {
    return false;
  }
  return !!node.children?.some(
    (child) => isColumnNode(child) && highlightColumnIds.has(child.id),
  );
};

// Children actually rendered, applying filteredGraph and the highlight subset
// (only on nodes that sit on the highlighted column's path). Shared by layout
// and height calculations so they never disagree.
const getDisplayedChildren = (
  node: EnrichedLineageNode,
  filteredGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
  highlightColumnIds?: Set<string>,
  unfilteredColumnParentId?: string,
): EnrichedLineageNode[] | undefined => {
  if (!node.children) return undefined;

  const skipHighlightFilter = !isNodeColumnHighlightFiltered(
    node,
    highlightColumnIds,
    unfilteredColumnParentId,
  );

  const filteredChildren = node.children.filter((child) => {
    if (filteredGraph && !filteredGraph.contains(child)) return false;
    if (!skipHighlightFilter && highlightColumnIds && isColumnNode(child)) {
      return highlightColumnIds.has(child.id);
    }
    return true;
  });

  // Return undefined (not []) so callers treat "none displayed" consistently.
  return filteredChildren.length > 0 ? filteredChildren : undefined;
};

const getNodeExpandedHeight = (
  node: EnrichedLineageNode,
  expandNodesIds?: string[],
  filteredGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
  highlightColumnIds?: Set<string>,
  unfilteredColumnParentId?: string,
) => {
  if (node.type === 'column') {
    return GRAPH_CHILD_NODE_HEIGHT;
  }

  // Don't fall back to node.children — that would over-size a node whose
  // columns are all filtered out (e.g. 0 connected columns during highlight).
  const nodeChildren = getDisplayedChildren(
    node,
    filteredGraph,
    highlightColumnIds,
    unfilteredColumnParentId,
  );
  const childCount = nodeChildren?.length;

  if (!childCount) {
    return GRAPH_NODE_HEIGHT;
  }

  const isGrandParent = isGradParentNode(node);

  const childNodeHeight = isGrandParent
    ? GRAPH_PARENT_NODE_HEIGHT
    : GRAPH_CHILD_NODE_HEIGHT;

  // The CSS Wrapper uses border-box with a 2px border (4px total).
  // Account for this so the CSS height leaves enough content area.
  const BORDER_OFFSET = 4;

  if (isGrandParent) {
    let childrenAreaHeight = 0;

    nodeChildren.forEach((child, i) => {
      const isExpanded = expandNodesIds?.includes(child.id);

      // Needs the parent height when it has loaded children or a non-zero
      // columnsCount (not yet lazy-loaded).
      const childHasChildrenSection =
        !!child.children || (child.columnsCount ?? 0) > 0;

      if (isExpanded) {
        const grandChildren = getDisplayedChildren(
          child,
          filteredGraph,
          highlightColumnIds,
          unfilteredColumnParentId,
        );
        childrenAreaHeight +=
          GRAPH_PARENT_NODE_HEIGHT +
          GRAPH_NODE_SEARCH_AREA_HEIGHT +
          (grandChildren?.length || 0) * GRAPH_CHILD_NODE_HEIGHT +
          BORDER_OFFSET;
      } else {
        // Grand-parent children always render as graphNode.
        // CSS renders collapsed parents at GRAPH_PARENT_NODE_HEIGHT + 4,
        // and collapsed non-parents at GRAPH_NODE_HEIGHT.
        childrenAreaHeight += childHasChildrenSection
          ? GRAPH_PARENT_NODE_HEIGHT + BORDER_OFFSET
          : GRAPH_NODE_HEIGHT;
      }

      // Add spacing before every child except the first (all grand-parent
      // children render as graphNode and need spacing between them).
      if (i > 0) {
        childrenAreaHeight += GRAPH_CHILD_NODE_VERTICAL_SPACING;
      }
    });

    return (
      GRAPH_PARENT_NODE_HEIGHT +
      GRAPH_NODE_SEARCH_AREA_HEIGHT +
      childrenAreaHeight +
      BORDER_OFFSET
    );
  }

  return (
    GRAPH_PARENT_NODE_HEIGHT +
    GRAPH_NODE_SEARCH_AREA_HEIGHT +
    childNodeHeight * childCount +
    BORDER_OFFSET
  );
};

const getChildrenYCoors = (
  children: EnrichedLineageNode[],
  expandedNodes: EnrichedLineageNode[],
  filteredGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
  isGrandParent?: boolean,
  highlightColumnIds?: Set<string>,
  unfilteredColumnParentId?: string,
) => {
  const yCoors: number[] = [];

  children.forEach((child, i) => {
    if (i === 0) {
      yCoors.push(GRAPH_PARENT_NODE_HEIGHT + GRAPH_NODE_SEARCH_AREA_HEIGHT);
    } else {
      const prevChild = children[i - 1];
      const isPrevExpanded = expandedNodes.some((n) => n.id === prevChild.id);

      // Renders a header when it has children or columnsCount > 0.
      const prevChildHasChildrenSection =
        !!prevChild.children || (prevChild.columnsCount ?? 0) > 0;

      const prevChildHeight = prevChildHasChildrenSection
        ? isPrevExpanded
          ? getNodeExpandedHeight(
              prevChild,
              expandedNodes.map((en) => en.id),
              filteredGraph,
              highlightColumnIds,
              unfilteredColumnParentId,
            )
          : GRAPH_PARENT_NODE_HEIGHT + 4 // matches CSS collapsed parent (border-box + border)
        : isGrandParent
        ? GRAPH_NODE_HEIGHT
        : GRAPH_CHILD_NODE_HEIGHT;

      const childHasChildrenSection =
        !!child.children || (child.columnsCount ?? 0) > 0;

      // Grand-parent children all render as graphNode and always need spacing.
      // Regular children only get spacing when they have sub-children.
      const needsSpacing = isGrandParent || childHasChildrenSection;
      const spacing = needsSpacing ? GRAPH_CHILD_NODE_VERTICAL_SPACING : 0;
      yCoors.push(yCoors[i - 1] + prevChildHeight + spacing);
    }
  });

  return yCoors;
};

export const getHighlightColumnIds = (
  workerData: WorkerData,
): Set<string> | undefined => {
  if (!workerData.highlightColumnSubgraph) return undefined;

  return new Set(
    workerData.highlightColumnSubgraph.nodes
      .filter((node) => isColumnNode(node))
      .map((node) => node.id),
  );
};

// The same asset appears as multiple EnrichedLineageNode instances (top-level
// plus a copy under each parent's `children`), so lazy-merge must update every
// instance or the others go stale.
const findAllEnrichedNodeInstances = (
  nodes: EnrichedLineageNode[],
  nodeId: string,
): EnrichedLineageNode[] => {
  const result: EnrichedLineageNode[] = [];
  for (const node of nodes) {
    if (node.id === nodeId) result.push(node);
    if (node.children) {
      result.push(...findAllEnrichedNodeInstances(node.children, nodeId));
    }
  }
  return result;
};

export const mergeAssetColumnsIntoWorkerData = (
  workerData: WorkerData,
  assetId: string,
  columns: ApiLineageNode[],
  // 'replace' swaps the parent's full column set (expand). 'additive' only
  // appends new columns (highlight — the connected subset is shown via display
  // filtering, never by destroying the loaded list).
  mode: 'replace' | 'additive' = 'replace',
) => {
  if (!workerData.enrichedLineageData) return;

  // Only 'replace' (expand) caches columns so they survive rebuilds. 'additive'
  // (highlight) columns are transient — caching would make them reappear and
  // accumulate after the highlight is cleared.
  if (mode === 'replace') {
    if (!workerData.lazyLoadedColumns) workerData.lazyLoadedColumns = {};
    const cachedLineageColumns = workerData.lazyLoadedColumns[assetId] || [];
    const cachedLineageColumnsByIds = new Map(
      cachedLineageColumns.map((column) => [column.id, column]),
    );
    columns.forEach((column) =>
      cachedLineageColumnsByIds.set(column.id, column),
    );
    workerData.lazyLoadedColumns[assetId] = Array.from(
      cachedLineageColumnsByIds.values(),
    );
  }

  const parentInstances = findAllEnrichedNodeInstances(
    workerData.enrichedLineageData.nodes,
    assetId,
  );
  if (parentInstances.length === 0) return;

  const sourceParent = parentInstances[0];
  const testExecutionsByColumn = sourceParent.testExecutions
    ? groupBy(
        sourceParent.testExecutions,
        (testExecution) => testExecution.columnName || '',
      )
    : undefined;

  const buildEnrichedColumns = (
    columnsToBuild: ApiLineageNode[],
  ): EnrichedLineageNode[] =>
    columnsToBuild.map((column) => {
      const enriched: EnrichedLineageNode = {
        ...column,
        label: column.label || column.id,
        parent: assetId,
      };
      if (testExecutionsByColumn) {
        enriched.testExecutions =
          testExecutionsByColumn[enriched.label || ''] || [];
      }
      return enriched;
    });

  // Each instance gets its own copy so mutations don't leak across the tree.
  parentInstances.forEach((parent) => {
    const existingChildren = parent.children || [];
    const existingColumnIds = new Set(
      existingChildren
        .filter((child) => isColumnNode(child))
        .map((child) => child.id),
    );

    if (mode === 'additive') {
      const newColumns = buildEnrichedColumns(
        columns.filter((column) => !existingColumnIds.has(column.id)),
      );
      if (newColumns.length) {
        parent.children = [...existingChildren, ...newColumns];
      }
    } else {
      const nonColumnChildren = existingChildren.filter(
        (child) => !isColumnNode(child),
      );
      parent.children = [
        ...nonColumnChildren,
        ...buildEnrichedColumns(columns),
      ];
    }
  });

  // Columns must also live in top-level nodes so initGraph inserts them into
  // the (filtered) graph.
  const existingNodeIds = new Set(
    workerData.enrichedLineageData.nodes.map((node) => node.id),
  );
  buildEnrichedColumns(columns).forEach((column) => {
    if (!existingNodeIds.has(column.id)) {
      workerData.enrichedLineageData!.nodes.push(column);
      existingNodeIds.add(column.id);
    }
  });

  workerData.graph = initGraph(workerData);
};

// Removes column ids from every parent instance and from the top-level nodes.
const removeColumnsFromParent = (
  workerData: WorkerData,
  parentId: string,
  columnIdsToRemove: Set<string>,
) => {
  if (!workerData.enrichedLineageData) return;

  findAllEnrichedNodeInstances(
    workerData.enrichedLineageData.nodes,
    parentId,
  ).forEach((parent) => {
    if (parent.children) {
      parent.children = parent.children.filter(
        (child) => !columnIdsToRemove.has(child.id),
      );
    }
  });

  workerData.enrichedLineageData.nodes =
    workerData.enrichedLineageData.nodes.filter(
      (node) => !columnIdsToRemove.has(node.id),
    );
};

// Removes transient columns from the previous highlight (those not in the
// lazyLoadedColumns cache). Without this, switching highlights accumulates
// columns and counts never revert.
const removePreviousHighlightColumns = (
  workerData: WorkerData,
  previousSubgraph: ApiLineage,
) => {
  if (!workerData.enrichedLineageData) return;

  const expandedColumnsByAsset = workerData.lazyLoadedColumns || {};
  const previousColumnsByParent = groupBy(
    previousSubgraph.nodes.filter(
      (node) => !!node.parent && isColumnNode(node),
    ),
    (node) => node.parent!,
  );

  Object.entries(previousColumnsByParent).forEach(([parentId, columns]) => {
    const expandedColumnIds = new Set(
      (expandedColumnsByAsset[parentId] || []).map((c) => c.id),
    );
    const columnIdsToRemove = new Set(
      columns.filter((c) => !expandedColumnIds.has(c.id)).map((c) => c.id),
    );
    if (!columnIdsToRemove.size) return;

    removeColumnsFromParent(workerData, parentId, columnIdsToRemove);
  });
};

// Removes the previous highlight's column-to-column edges (not asset edges,
// which belong to the base graph) so they don't accumulate across switches.
const removePreviousHighlightColumnToColumnEdges = (
  workerData: WorkerData,
  previousSubgraph: ApiLineage,
) => {
  if (!workerData.enrichedLineageData) return;

  const previousColumnIds = new Set(
    previousSubgraph.nodes
      .filter((node) => isColumnNode(node))
      .map((node) => node.id),
  );
  const previousHighlightEdgeKeys = new Set(
    previousSubgraph.edges
      .filter(
        ([dst, src]) =>
          previousColumnIds.has(dst) && previousColumnIds.has(src),
      )
      .map((edge) => JSON.stringify(edge)),
  );
  if (!previousHighlightEdgeKeys.size) return;

  workerData.enrichedLineageData.edges =
    workerData.enrichedLineageData.edges.filter(
      (edge) => !previousHighlightEdgeKeys.has(JSON.stringify(edge)),
    );
};

export const mergeHighlightColumnSubgraphIntoWorkerData = (
  workerData: WorkerData,
  subgraph?: ApiLineage,
) => {
  const previousSubgraph = workerData.highlightColumnSubgraph;
  workerData.highlightColumnSubgraph = subgraph;

  if (!workerData.enrichedLineageData) return;

  // Drop the previous highlight's transient columns and edges so they don't
  // accumulate across switches.
  if (previousSubgraph) {
    removePreviousHighlightColumns(workerData, previousSubgraph);
    removePreviousHighlightColumnToColumnEdges(workerData, previousSubgraph);
  }

  if (!subgraph) {
    workerData.graph = initGraph(workerData);
    return;
  }

  const columnsByParent = groupBy(
    subgraph.nodes.filter((node) => !!node.parent && isColumnNode(node)),
    (node) => node.parent!,
  );

  Object.entries(columnsByParent).forEach(([parentId, columns]) => {
    // Additive so an expanded node's loaded columns aren't discarded — the
    // connected-only view is achieved via display filtering.
    mergeAssetColumnsIntoWorkerData(workerData, parentId, columns, 'additive');
  });

  const existingEdges = new Set(
    workerData.enrichedLineageData.edges.map((edge) => JSON.stringify(edge)),
  );

  subgraph.edges.forEach((edge) => {
    const edgeKey = JSON.stringify(edge);
    if (!existingEdges.has(edgeKey)) {
      workerData.enrichedLineageData!.edges.push(edge);
      existingEdges.add(edgeKey);
    }
  });

  workerData.graph = initGraph(workerData);
};

// Re-applies lazy-loaded columns after a getGraph rebuild (assets-only payload)
// drops them, so expanded columns survive highlight/hop changes.
export const reapplyLazyLoadedColumns = (workerData: WorkerData) => {
  const cachedLineaegeColumnsById = workerData.lazyLoadedColumns;
  if (!cachedLineaegeColumnsById || !workerData.enrichedLineageData) return;

  const lineageColumnIds = Object.keys(cachedLineaegeColumnsById);
  if (lineageColumnIds.length === 0) return;

  lineageColumnIds.forEach((assetId) => {
    const columns = cachedLineaegeColumnsById[assetId];
    if (columns?.length) {
      mergeAssetColumnsIntoWorkerData(workerData, assetId, columns, 'replace');
    }
  });
};

// Create the child nodes and align their Y coor inside their parent node
// They're automatically positioned relative to their parents by 'reactflow'
const getLayoutedChildNodes = (
  node: EnrichedLineageNode,
  rootNodes: EnrichedLineageNode[],
  filteredGraph:
    | MutableUnweightedGraph<EnrichedLineageNode, string>
    | undefined,
  width: number,
  getChildNodeTotals: (
    childNode: EnrichedLineageNode,
  ) => Record<string, number> | undefined,
  expandedNodes: EnrichedLineageNode[],
  highlightColumnIds?: Set<string>,
  // The highlighted column's own parent keeps its full column list (not the
  // connected subset).
  unfilteredColumnParentId?: string,
): ReactFlowNode<ReactFlowNodeData>[] | undefined => {
  if (node.isRestricted === true) {
    return undefined;
  }

  const filteredChildren = getDisplayedChildren(
    node,
    filteredGraph,
    highlightColumnIds,
    unfilteredColumnParentId,
  );

  const isGrandParent = isGradParentNode(node);

  const childrenYCoors = getChildrenYCoors(
    filteredChildren || [],
    expandedNodes,
    filteredGraph,
    isGrandParent,
    highlightColumnIds,
    unfilteredColumnParentId,
  );

  return filteredChildren?.map((child, i) => {
    // Children of a grand-parent node (e.g. chart tiles inside a dashboard)
    // always render as graphNode so they get borders, icons and an expand
    // button — even when they have no sub-children (columns) yet.
    const renderAsGraphNode = !!child.children || isGrandParent;

    return {
      id: child.id,
      type: renderAsGraphNode ? 'graphNode' : 'graphChildNode',
      position: {
        x: renderAsGraphNode ? CHILD_NODE_PADDING : 0,
        y: childrenYCoors[i],
      },
      parentNode: child.parent,
      draggable: false,
      deletable: false,
      data: {
        metadata: child,
        isRootNode: rootNodes.some((rootNode) => rootNode.id === child.id),
        height: getNodeExpandedHeight(
          child,
          expandedNodes.map((en) => en.id),
          filteredGraph,
          highlightColumnIds,
          unfilteredColumnParentId,
        ),
        width: renderAsGraphNode ? width - CHILD_NODE_PADDING * 2 : width,
        parentNode: { ...node, children: filteredChildren },
        totals: getChildNodeTotals(child),
        isExpanded: expandedNodes.some((n) => n.id === child.id),
        columnsCount: child.columnsCount,
        isColumnHighlightFiltered: isNodeColumnHighlightFiltered(
          child,
          highlightColumnIds,
          unfilteredColumnParentId,
        ),
        children: child.children
          ? getLayoutedChildNodes(
              child,
              rootNodes,
              filteredGraph,
              renderAsGraphNode ? width - CHILD_NODE_PADDING * 2 : width,
              getChildNodeTotals,
              expandedNodes,
              highlightColumnIds,
              unfilteredColumnParentId,
            )
          : undefined,
      },
    };
  });
};

const getNodeChildrenRecursively = (node: EnrichedLineageNode) => {
  const result: EnrichedLineageNode[] = [];
  if (node.children) {
    node.children.forEach((child) => {
      result.push(child, ...getNodeChildrenRecursively(child));
    });
  }
  return result;
};

export const getReactFlowNodeChildrenRecursively = (
  node: ReactFlowNode<ReactFlowNodeData>,
) => {
  const result: ReactFlowNode<ReactFlowNodeData>[] = [];
  if (node.data.children) {
    node.data.children.forEach((child) => {
      result.push(child, ...getReactFlowNodeChildrenRecursively(child));
    });
  }
  return result;
};

export const getReactFlowNodeParentIds = (
  node: ReactFlowNode<ReactFlowNodeData>,
) =>
  [node?.data.parentNode?.id, node?.data.parentNode?.parent].filter(
    (id) => !!id,
  ) as string[];

export const getNodeParentsRecursively = (
  node: EnrichedLineageNode,
  nodePool: EnrichedLineageNode[],
) => {
  const result: EnrichedLineageNode[] = [];
  if (node.parent) {
    const parentNode = nodePool.find((n) => n.id === node.parent);
    if (parentNode) {
      result.push(
        parentNode,
        ...getNodeParentsRecursively(parentNode, nodePool),
      );
    }
  }
  return result;
};

const getExpandedNodes = (
  workerData: WorkerData,
  highlightedNodeId?: string,
  expandNodesIds?: string[],
  collapseNodesIds?: string[],
  selectedNodes?: string[],
) => {
  const { filteredGraph } = workerData;

  if (!filteredGraph) return [];

  const rootNodes = filteredGraph
    .nodes()
    .filter((n) => n.id === highlightedNodeId || selectedNodes?.includes(n.id));

  const isSelectedChildNode = rootNodes.some((n) => !!n.parent);

  if (!isSelectedChildNode)
    return filteredGraph
      .nodes()
      .filter(
        (n) =>
          !n.isRestricted &&
          !collapseNodesIds?.includes(n.id) &&
          expandNodesIds?.includes(n.id),
      );

  const childrenOnPath = getNodesOnPath(workerData, rootNodes, filteredGraph);

  const nodesToExpand = filteredGraph
    .nodes()
    .filter(
      (n) =>
        !n.isRestricted &&
        !collapseNodesIds?.includes(n.id) &&
        (expandNodesIds?.includes(n.id) ||
          getNodeChildrenRecursively(n)?.some(
            (child) =>
              child.id === highlightedNodeId ||
              selectedNodes?.includes(child.id) ||
              childrenOnPath.some((c) => c.id === child.id),
          )),
    );

  return nodesToExpand;
};

export const highlightNode = (
  workerData: WorkerData,
  nodeId: string | undefined,
  reactFlowGraph: ReactFlowGraph,
) => {
  const { filteredGraph } = workerData;

  if (!filteredGraph) return reactFlowGraph;

  const node = filteredGraph.nodes().find((n) => n.id === nodeId);

  if (!nodeId || !node) {
    reactFlowGraph.edges = reactFlowGraph.edges.map((e) => ({
      ...e,
      zIndex: 0,
      style: { ...e.style, ...edgeStyle.default },
    }));
    reactFlowGraph.nodes = reactFlowGraph.nodes.map((n) => ({
      ...n,
      data: { ...n.data, isOnHighlightPath: false },
    }));
    return reactFlowGraph;
  }

  const edgesOnPath = getEdgesOnPath(workerData, [node], filteredGraph);
  const nodesOnPath = getNodesOnPath(workerData, [node], filteredGraph);

  const styledEdges = reactFlowGraph.edges.map((rfe) => {
    const logicalEdges = rfe.data?.logicalEdges ?? [
      { source: rfe.source, target: rfe.target },
    ];
    const isOnPath = edgesOnPath.some((e) =>
      logicalEdges.some(
        (logicalEdge) =>
          logicalEdge.source === e.source.id &&
          logicalEdge.target === e.target.id,
      ),
    );

    return {
      ...rfe,
      zIndex: isOnPath ? 1 : 0,
      style: isOnPath
        ? { ...rfe.style, ...edgeStyle.highlighted }
        : { ...rfe.style, ...edgeStyle.greyedOut },
    };
  });

  const nodeIdsOnHighlightedEdges = new Set<string>();
  styledEdges.forEach((edge) => {
    if (edge.zIndex === 1) {
      nodeIdsOnHighlightedEdges.add(edge.source);
      nodeIdsOnHighlightedEdges.add(edge.target);
    }
  });

  const updatedNodes = reactFlowGraph.nodes.map((rfn) => {
    const childrenOnPath = rfn.data.children?.some((child) =>
      nodesOnPath.some((n) => n.id === child.id),
    );
    return {
      ...rfn,
      data: {
        ...rfn.data,
        isOnHighlightPath:
          nodesOnPath.some((n) => rfn.id === n.id) ||
          childrenOnPath ||
          nodeIdsOnHighlightedEdges.has(rfn.id),
        children: rfn.data.children?.map((c) => ({
          ...c,
          data: {
            ...c.data,
            isOnHighlightPath: nodesOnPath.some((n) => c.id === n.id),
          },
        })),
      },
    };
  });

  return { nodes: updatedNodes, edges: styledEdges };
};

// Hides child-child edges if there is a parent-child edge.
// Shifts child-child edges to a parent-child edge if node is collapsed.
export const hideAndShiftGraphEdges = (
  workerData: WorkerData,
  curReactFlowGraph: ReactFlowGraph,
  selectedNodes?: string[],
  highlightedNodeId?: string,
) => {
  // todo: implement more efficiently (in addition to adding 'root' arg)
  const { filteredGraph } = workerData;
  if (!filteredGraph) return curReactFlowGraph;

  const highlightedNode = filteredGraph
    .nodes()
    .find((n) => n.id === highlightedNodeId);

  const filteredChildNodes = selectedNodes?.length
    ? filteredGraph
        .nodes()
        .filter((n) => n.parent && selectedNodes?.includes(n.id))
    : [];

  const hideChildrenEdges =
    (!highlightedNode || !highlightedNode?.parent) &&
    !filteredChildNodes?.length;

  if (hideChildrenEdges) {
    const hiddenChildren = curReactFlowGraph.edges.map((e) => {
      const sourceNode = curReactFlowGraph.nodes.find((n) => n.id === e.source);
      const targeteNode = curReactFlowGraph.nodes.find(
        (n) => n.id === e.target,
      );
      const isChild = sourceNode?.parentNode || targeteNode?.parentNode;
      return { ...e, hidden: !!isChild };
    });
    return { ...curReactFlowGraph, edges: hiddenChildren };
  }

  const rootNodes = [...filteredChildNodes, highlightedNode].filter(
    (n) => !!n,
  ) as EnrichedLineageNode[];

  const edgesOnChildNodePath = getEdgesOnPath(
    workerData,
    rootNodes,
    filteredGraph,
  );

  // since we're filtered on a child node, hide all child-child edges that are not on path
  const hiddenRedundantEdges = filteredGraph.edges().map((e) => {
    const source = getRenderedEdgeNodeId(e.source);
    const target = getRenderedEdgeNodeId(e.target);
    const isChildToChild = e.source.parent && e.target.parent;

    const reactFlowEdge = {
      id: `${source}-${target}`,
      source,
      target,
      style: isChildToChild ? edgeStyle.child : edgeStyle.parent,
      data: {
        logicalEdges: [{ source: e.source.id, target: e.target.id }],
      },
    };

    if (isChildToChild) {
      const isOnPath = edgesOnChildNodePath.some(
        (eop) => eop.source.id === e.source.id && eop.target.id === e.target.id,
      );
      return {
        ...reactFlowEdge,
        hidden: !isOnPath,
      } as ReactFlowEdge;
    }

    const isRedundant = edgesOnChildNodePath.some(
      (eop) =>
        eop.source.parent === e.source.id && eop.target.parent === e.target.id,
    );

    return {
      ...reactFlowEdge,
      hidden: isRedundant,
    } as ReactFlowEdge;
  });

  const graphWithHiddenEdges = {
    nodes: curReactFlowGraph.nodes,
    edges: dedupeReactFlowEdges(hiddenRedundantEdges),
  };

  const highlightedNodeGraph = highlightNode(
    workerData,
    highlightedNodeId,
    graphWithHiddenEdges,
  );

  // todo: *** slow operation *** must improve this.
  // edges should hold actual nodes as source/target
  // and not strings.
  const childToChildEdges = highlightedNodeGraph.edges.filter((e) => {
    if (e.hidden) return false;

    const sourceNode = filteredGraph.nodes().find((n) => n.id === e.source);
    const targetNode = filteredGraph.nodes().find((n) => n.id === e.target);
    return sourceNode?.parent && targetNode?.parent;
  });
  // *** end of slow operation ***

  // Shifted edges are child->parent or parent->child edges
  const shiftedChildEdges = childToChildEdges
    .map((e) => {
      const sourceChild = filteredGraph.nodes().find((n) => n.id === e.source);
      const targetChild = filteredGraph.nodes().find((n) => n.id === e.target);

      const sourceParent = curReactFlowGraph.nodes.find(
        (n) => n.id === sourceChild?.parent,
      );

      const targetParent = curReactFlowGraph.nodes.find(
        (n) => n.id === targetChild?.parent,
      );

      const sourceParentHasHighlightedChild = sourceParent?.data.children?.some(
        (c) => c.data.isOnHighlightPath,
      );

      if (sourceParent?.data.isExpanded && !targetParent?.data.isExpanded) {
        return {
          id: `${targetParent?.id}-${sourceChild?.id}`,
          source: sourceChild?.id,
          target: targetParent?.id,
          data: e.data,
          style: sourceParentHasHighlightedChild
            ? { ...e.style, ...edgeStyle.highlighted }
            : e.style,
        } as ReactFlowEdge;
      }

      if (!sourceParent?.data.isExpanded && targetParent?.data.isExpanded) {
        return {
          id: `${targetChild?.id}-${sourceParent?.id}`,
          source: sourceParent?.id,
          target: targetChild?.id,
          data: e.data,
          style: e.style,
        } as ReactFlowEdge;
      }

      if (!sourceParent?.data.isExpanded && !targetParent?.data.isExpanded) {
        return {
          id: `${sourceParent?.id}-${sourceParent?.id}`,
          source: sourceParent?.id,
          target: targetParent?.id,
          data: e.data,
          style: e.style,
        } as ReactFlowEdge;
      }

      return e;
    })
    .filter((e) => !!e) as ReactFlowEdge[];

  const distictShiftedChildEdges = distinctArrayBy(shiftedChildEdges, 'id');

  const combinedEdges = [
    ...highlightedNodeGraph.edges.filter(
      (e) => !childToChildEdges.find((ce) => ce.id === e.id),
    ),
    ...distictShiftedChildEdges,
  ];

  return {
    nodes: highlightedNodeGraph.nodes,
    edges: dedupeReactFlowEdges(combinedEdges),
  };
};

const getNodeWidth = (label: string, totals?: Record<string, number>) => {
  const offscreenCanvas = new OffscreenCanvas(1, 1);
  const offscreenContext = offscreenCanvas.getContext('2d');

  const padding = 32;
  const totalsGap = 4;
  const totalsChipWidth = 24;
  const totalsLeftMargin = 24;

  const totalsCount =
    totals && Object.keys(totals).filter((key) => totals[key] > 0).length;

  const totalsWidth = totalsCount
    ? totalsCount * totalsChipWidth +
      (totalsCount - 1) * totalsGap +
      totalsLeftMargin
    : 0;

  if (!offscreenContext) {
    return GRAPH_NODE_MIN_WIDTH;
  }

  // somehow 18px works more accurately although the actual font size is 16px
  offscreenContext.font = 'bold 18px "DM Sans", sans-serif';
  const textWidth = offscreenContext.measureText(label).width;
  const width = padding + textWidth + totalsWidth;
  return Math.min(Math.max(width, GRAPH_NODE_MIN_WIDTH), GRAPH_NODE_MAX_WIDTH);
};

const getDepthXCoor = (
  depthWidthMap: Record<string, number>,
  curDepth: string,
) => {
  if (curDepth === '1') {
    return 0;
  }

  const depths = Object.keys(depthWidthMap);
  const prevDepths = depths.filter((d) => Number(d) < Number(curDepth));
  const prevWidthsArr = prevDepths.map((d) => depthWidthMap[d]);
  const prevWidthsSum = sumArr(prevWidthsArr);
  return (
    Number(prevWidthsSum) + prevWidthsArr.length * GRAPH_NODE_HORIZONTAL_SPACING
  );
};

const getTopLevelAncestor = (
  node: EnrichedLineageNode,
  allNodes: EnrichedLineageNode[],
): EnrichedLineageNode => {
  if (!node.parent) return node;
  const parentNode = allNodes.find((n) => n.id === node.parent);
  if (!parentNode) return node;
  return getTopLevelAncestor(parentNode, allNodes);
};

export const calculateNodeDepth = (
  subGraph: MutableUnweightedGraph<EnrichedLineageNode, string>,
  nodeId: EnrichedLineageNode,
  memo: Map<EnrichedLineageNode, number> = new Map(),
  visited: Set<EnrichedLineageNode> = new Set(),
  cycleNodes: Set<string> = new Set(),
  fromNode?: EnrichedLineageNode,
): number => {
  // If the node's depth is already calculated, return it
  if (memo.has(nodeId)) {
    return memo.get(nodeId)!;
  }

  // If the node is being visited again there's a loop in the graph. Record both
  // endpoints of the back-edge (the revisited node and the node we came from)
  // and treat it as contributing no depth instead of throwing, so a cyclic
  // lineage graph still renders (best-effort) rather than crashing the panel.
  if (visited.has(nodeId)) {
    cycleNodes.add(nodeId.id);
    if (fromNode) cycleNodes.add(fromNode.id);
    return 0;
  }

  // Mark the node as visited
  visited.add(nodeId);

  // Initialize the depth to 0
  let depth = 0;

  // Get the incoming edges for the node
  const edges = subGraph.incomingEdgesOf(nodeId);

  // Iterate over the edges to calculate depths of source nodes
  for (const edge of edges) {
    const sourceNode = edge.source;
    const sourceNodeDepth = calculateNodeDepth(
      subGraph,
      sourceNode,
      memo,
      visited,
      cycleNodes,
      nodeId,
    );
    depth = Math.max(depth, sourceNodeDepth);
  }

  // For top-level nodes, also consider incoming edges of descendants whose
  // sources belong to a different top-level subtree (e.g. a column inside a
  // semantic view element that is downstream from another model's column).
  if (!nodeId.parent) {
    const allGraphNodes = subGraph.nodes();
    const descendants = getNodeChildrenRecursively(nodeId);
    for (const descendant of descendants) {
      if (!subGraph.contains(descendant)) continue;
      for (const edge of subGraph.incomingEdgesOf(descendant)) {
        const sourceTopLevel = getTopLevelAncestor(edge.source, allGraphNodes);
        if (sourceTopLevel.id !== nodeId.id && !visited.has(sourceTopLevel)) {
          const sourceDepth = calculateNodeDepth(
            subGraph,
            sourceTopLevel,
            memo,
            visited,
            cycleNodes,
            nodeId,
          );
          depth = Math.max(depth, sourceDepth);
        }
      }
    }
  }

  // Unmark the node as visited (important for other branches of the graph)
  visited.delete(nodeId);

  // Increment depth to account for the current node
  const resultDepth = depth + 1;

  // Cache the calculated depth
  memo.set(nodeId, resultDepth);

  return resultDepth;
};

export const getReactFlowGraph = (
  workerData: WorkerData,
  rootNodes: EnrichedLineageNode[],
  getNodeTotals: (
    n: EnrichedLineageNode,
    filters?: UrlFilters,
    testResultsData?: Record<string, TestResult[]>,
    resultsTotalsData?: Record<string, Totals>,
  ) => Record<string, number> | undefined,
  curReactFlowGraph?: ReactFlowGraph,
  lockPosNodes?: string[],
  highlightedNodeId?: string,
  expandNodesIds?: string[],
  collapseNodesIds?: string[],
  selectedNodes?: string[],
  reportData?: Report,
  filters?: UrlFilters,
  onCycleDetected?: (cycleNodes: string[]) => void,
  precomputedRenderedEdges?: ReturnType<
    typeof buildRenderedEdgesFromFilteredGraph
  >,
) => {
  const { filteredGraph } = workerData;

  if (!filteredGraph) return { nodes: [], edges: [] };

  const nodesWithoutChildren = filteredGraph
    .nodes()
    .filter((node) => !node.parent);

  // Group the nodes by depth
  const cycleNodes = new Set<string>();
  const groupedByDepth = groupBy(nodesWithoutChildren, (node) =>
    calculateNodeDepth(
      filteredGraph,
      node,
      new Map(),
      new Set(),
      cycleNodes,
    ).toString(),
  );

  if (cycleNodes.size > 0) {
    const cycleNodeIds = [...cycleNodes];
    // eslint-disable-next-line no-console
    console.warn(
      `Lineage graph contains a cycle; rendering best-effort layout. Nodes on cycle: ${cycleNodeIds.join(
        ', ',
      )}`,
    );
    onCycleDetected?.(cycleNodeIds);
  }

  // Get the depths and the number of nodes for each depth
  const depths = Object.keys(groupedByDepth).sort(
    (a, b) => Number(a) - Number(b),
  );
  const nodeCounts = depths.map((depth) => groupedByDepth[depth].length);

  const expandedNodes = getExpandedNodes(
    workerData,
    highlightedNodeId,
    expandNodesIds,
    collapseNodesIds,
    selectedNodes,
  );

  const highlightColumnIds = getHighlightColumnIds(workerData);

  // Highlighted column's own parent shows all columns; other nodes show only
  // the connected ones.
  const unfilteredColumnParentId = highlightColumnIds
    ? workerData.enrichedLineageData?.nodes.find(
        (n) => n.id === highlightedNodeId,
      )?.parent
    : undefined;

  const depthWidthMap: Record<string, number> = {};

  // Initialize an array to store the layouted nodes
  const layoutedNodes: ReactFlowNode<ReactFlowNodeData>[] = [];

  // Iterate over the depths
  for (let i = 0; i < depths.length; i += 1) {
    const layoutedDepthNodes: ReactFlowNode<ReactFlowNodeData>[] = [];
    const depthXCoor = getDepthXCoor(depthWidthMap, depths[i]);
    depthWidthMap[depths[i]] = 0;

    // Iterate over the nodes at the depth
    for (let j = 0; j < nodeCounts[i]; j += 1) {
      const node = groupedByDepth[depths[i]][j];
      const nodeHeight = getNodeExpandedHeight(
        node,
        expandedNodes.map((en) => en.id),
        filteredGraph,
        highlightColumnIds,
        unfilteredColumnParentId,
      );

      const {
        test_results: testResultsData,
        test_results_totals: resultsTotalsData,
      } = reportData || {};

      const nodeTotals = getNodeTotals(
        node,
        filters,
        testResultsData,
        resultsTotalsData,
      );

      const getChildNodeTotals = (childNode: EnrichedLineageNode) =>
        getNodeTotals(childNode, filters, testResultsData, resultsTotalsData);

      const nodeWidth = getNodeWidth(node.label || '', nodeTotals);
      depthWidthMap[depths[i]] = Math.max(depthWidthMap[depths[i]], nodeWidth);

      const isExpanded = expandedNodes?.some((n) => n.id === node.id);
      const safeIsExpanded = !node.isRestricted && Boolean(isExpanded);

      const lockedNodePos = lockPosNodes?.includes(node.id)
        ? curReactFlowGraph?.nodes.find((n) => n.id === node.id)?.position
        : undefined;

      const y =
        lockedNodePos?.y ||
        getNodeYCoor(
          node,
          filteredGraph,
          layoutedNodes,
          layoutedDepthNodes,
          i + 1,
          j,
        );

      layoutedDepthNodes.push({
        id: node.id,
        type: 'graphNode',
        position: {
          x: lockedNodePos?.x || depthXCoor,
          y,
        },
        parentNode: node.parent,
        draggable: !node.parent,
        deletable: false,
        zIndex:
          safeIsExpanded && !node.parent ? EXPANDED_NODE_Z_INDEX : NODE_Z_INDEX,
        data: {
          metadata: node,
          children: getLayoutedChildNodes(
            node,
            rootNodes,
            filteredGraph,
            nodeWidth,
            getChildNodeTotals,
            expandedNodes,
            highlightColumnIds,
            unfilteredColumnParentId,
          ),
          totals: nodeTotals,
          isRootNode:
            !!selectedNodes?.includes(node.id) ||
            intersectArrays(
              selectedNodes || [],
              node?.children?.map((c) => c.id) || [],
            ).length > 0,
          isFiltered: !!rootNodes.find((rootNode) => rootNode.id === node.id),
          height: nodeHeight,
          width: nodeWidth,
          isExpanded: safeIsExpanded,
          columnsCount: node.columnsCount,
          isColumnHighlightFiltered: isNodeColumnHighlightFiltered(
            node,
            highlightColumnIds,
            unfilteredColumnParentId,
          ),
        },
      });
    }

    const layoutedDepthNodesNoOverlap = alignOverlappingNodes(
      layoutedDepthNodes,
      lockPosNodes,
    );

    const nodesWithChildren = layoutedDepthNodesNoOverlap.flatMap((n) => [
      n,
      ...getReactFlowNodeChildrenRecursively(n),
    ]);

    nodesWithChildren.forEach((n) => {
      layoutedNodes.push(n);
    });
  }

  const renderedEdges =
    precomputedRenderedEdges ??
    buildRenderedEdgesFromFilteredGraph(filteredGraph);

  return { nodes: layoutedNodes, edges: renderedEdges };
};

export const getStreamNodes = (
  workerData: WorkerData,
  direction: 'upstream' | 'downstream',
  subGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>,
  rootNodes?: EnrichedLineageNode[],
  includeChildren: boolean = true,
  depthLimit?: number,
) => {
  if (
    !rootNodes ||
    rootNodes.length === 0 ||
    !workerData.graph ||
    !workerData.enrichedLineageData
  )
    return [];

  const nodesOnPath = getStreamDirectionNodes(
    workerData,
    rootNodes,
    direction,
    subGraph || workerData.graph,
    depthLimit,
  );

  if (!includeChildren) {
    return distinctArrayBy(nodesOnPath, 'id');
  }

  const nodesWithChildren = nodesOnPath
    .map((n) => (n.children ? [n, ...n.children] : n))
    .flat();

  return distinctArrayBy(nodesWithChildren, 'id');
};

const appendParentsAndChildren = (
  nodes: EnrichedLineageNode[],
  nodePool: EnrichedLineageNode[],
) =>
  nodes.flatMap((n) => {
    const relatives = [n];
    const allParents = getNodeParentsRecursively(n, nodePool);
    if (allParents) {
      relatives.push(...allParents);
    }
    if (n.children) {
      const allChildren = getNodeChildrenRecursively(n);
      relatives.push(...allChildren);
    }
    return relatives;
  });

export const getGraphNodes = (
  workerData: WorkerData,
  rootNodes: EnrichedLineageNode[],
  isFiltersApplied: boolean,
  nodePool: EnrichedLineageNode[],
): EnrichedLineageNode[] => {
  if (!workerData.enrichedLineageData || !workerData.graph) return [];

  debugLog('*********** getGraphNodes start ***********');
  const totalStart = performance.now();
  let start = performance.now();

  const subGraph = nodePool
    ? GraphUtil.functional.subset(workerData.graph, nodePool)
    : workerData.graph;

  debugLog('getGraphNodes - subGraph: ', performance.now() - start);
  start = performance.now();

  const graphNodes = isFiltersApplied
    ? getNodesOnPath(workerData, rootNodes, subGraph, true)
    : nodePool;

  debugLog('graphNodes: ', performance.now() - start);
  start = performance.now();

  const nodesWithParentsAndChildren = appendParentsAndChildren(
    graphNodes,
    workerData.enrichedLineageData.nodes,
  );

  debugLog('nodesWithParentsAndChildren: ', performance.now() - start);
  debugLog(
    '*********** getGraphNodes end ***********',
    performance.now() - totalStart,
  );

  return distinctArrayBy(nodesWithParentsAndChildren, 'id');
};

export const applyHopLimit = (
  workerData: WorkerData,
  subGraph: MutableUnweightedGraph<EnrichedLineageNode, string>,
  rootNodes: EnrichedLineageNode[],
  hopLimit: HopLimit,
) => {
  const upstream = getStreamNodes(
    workerData,
    'upstream',
    subGraph,
    rootNodes,
    false,
    hopLimit?.upstream,
  );

  const downstream = getStreamNodes(
    workerData,
    'downstream',
    subGraph,
    rootNodes,
    false,
    hopLimit?.downstream,
  );

  const rootsWithParentsAndChildren = appendParentsAndChildren(
    [...rootNodes, ...upstream, ...downstream],
    subGraph.nodes(),
  );

  return distinctArrayBy(rootsWithParentsAndChildren, 'id');
};

export const toggleExpandedNode = (
  node: ReactFlowNode<ReactFlowNodeData>,
  curReactFlowGraph: ReactFlowGraph,
) => {
  const shouldExpand = !node.data.isExpanded;

  const updatedNode = {
    ...node,
    zIndex: node.parentNode
      ? node.zIndex
      : shouldExpand
      ? EXPANDED_NODE_Z_INDEX
      : NODE_Z_INDEX,
    data: { ...node.data, isExpanded: shouldExpand },
  };

  const nodeChildren = node.data.children;

  let updatedNodes = curReactFlowGraph.nodes.map((n) =>
    n.id === node.id ? updatedNode : n,
  );

  if (shouldExpand && nodeChildren) {
    const descendants = nodeChildren.flatMap((child) =>
      child.data.isExpanded && child.data.children
        ? [child, ...child.data.children]
        : [child],
    );
    const descendantIds = new Set(descendants.map((d) => d.id));
    updatedNodes = updatedNodes.filter((n) => !descendantIds.has(n.id));
    const nodeIdx = updatedNodes.findIndex((n) => n.id === node.id);
    updatedNodes.splice(nodeIdx + 1, 0, ...descendants);
  }

  // When expanding/collapsing a sub-node inside a grandparent, update sibling
  // Y positions and the grandparent's height to prevent overlap.
  if (node.parentNode) {
    const parentIdx = updatedNodes.findIndex((n) => n.id === node.parentNode);
    if (parentIdx !== -1) {
      const parentNode = updatedNodes[parentIdx];
      if (isGradParentNode(parentNode.data.metadata)) {
        const parentChildren = parentNode.data.children || [];

        // Build the list of expanded children metadata, accounting for the
        // node that is currently being toggled AND any previously expanded
        // siblings (whose state lives in updatedNodes, not stale parent data).
        const expandedChildrenMeta = parentChildren
          .filter((child) => {
            if (child.id === node.id) return shouldExpand;
            const liveNode = updatedNodes.find((n) => n.id === child.id);
            return !!liveNode?.data.isExpanded;
          })
          .map((child) => child.data.metadata);

        const expandedIds = expandedChildrenMeta.map((m) => m.id);

        // Reuse the authoritative layout functions to stay consistent with
        // the initial graph layout calculations.
        const childrenMeta = parentChildren.map((c) => c.data.metadata);
        const yCoors = getChildrenYCoors(
          childrenMeta,
          expandedChildrenMeta,
          undefined,
          true,
        );

        // Update each child's Y position
        parentChildren.forEach((child, i) => {
          const childIdx = updatedNodes.findIndex((n) => n.id === child.id);
          if (childIdx !== -1) {
            updatedNodes[childIdx] = {
              ...updatedNodes[childIdx],
              position: {
                ...updatedNodes[childIdx].position,
                y: yCoors[i],
              },
            };
          }
        });

        // Recalculate parent height using the same function used at layout time
        const newHeight = getNodeExpandedHeight(
          parentNode.data.metadata,
          expandedIds,
        );

        // Update the parent's children array with live node data so that
        // subsequent toggles see the correct expansion states.
        const updatedParentChildren = parentChildren.map((child) => {
          const liveNode = updatedNodes.find((n) => n.id === child.id);
          return liveNode || child;
        });

        updatedNodes[parentIdx] = {
          ...parentNode,
          data: {
            ...parentNode.data,
            height: newHeight,
            children: updatedParentChildren,
          },
        };
      }
    }
  }

  return { nodes: updatedNodes, edges: curReactFlowGraph.edges };
};

export const filterReactFlowChildNodes = (
  parentNode: ReactFlowNode<ReactFlowNodeData, string | undefined>,
  search: string,
  curReactFlowGraphNodes: ReactFlowNode<
    ReactFlowNodeData,
    string | undefined
  >[],
) => {
  const emptySearch = !search?.trim();

  const childrenToHide = emptySearch
    ? []
    : parentNode.data.children?.filter(
        (c) =>
          !c.data.metadata.label?.toLowerCase().includes(search.toLowerCase()),
      );

  const childrenToHideIds = childrenToHide?.map((n) => n.id);

  const childrenToShow = emptySearch
    ? parentNode.data.children
    : parentNode.data.children?.filter((c) =>
        c.data.metadata.label?.toLowerCase().includes(search.toLowerCase()),
      );

  const childrenToShowIds = childrenToShow?.map((n) => n.id);

  const expandedChildrenToShow = emptySearch
    ? parentNode.data.children
        ?.filter((n) => n.data.isExpanded)
        .map((n) => n.data.metadata)
    : parentNode.data.children
        ?.filter(
          (c) =>
            c.data.metadata.label
              ?.toLowerCase()
              .includes(search.toLowerCase()) && c.data.isExpanded,
        )
        .map((n) => n.data.metadata);

  const isGrandParent = isGradParentNode(parentNode.data.metadata);
  const childrenYCoors = getChildrenYCoors(
    childrenToShow?.map((n) => n.data.metadata) || [],
    expandedChildrenToShow || [],
    undefined,
    isGrandParent,
  );

  return curReactFlowGraphNodes.map((n) => {
    if (
      childrenToHideIds?.includes(n.id) ||
      childrenToHideIds?.includes(n.parentNode || '')
    ) {
      return { ...n, hidden: true };
    }

    if (childrenToShowIds?.includes(n.id)) {
      const index = childrenToShowIds.findIndex((cid) => cid === n.id);
      return {
        ...n,
        hidden: false,
        position: {
          ...n.position,
          y: childrenYCoors[index],
        },
      };
    }

    if (childrenToShowIds?.includes(n.parentNode || '')) {
      const parentToShow = curReactFlowGraphNodes.find(
        (gn) => gn.id === n.parentNode,
      );
      if (parentToShow?.data.isExpanded) {
        return { ...n, hidden: false };
      }
    }

    return n;
  });
};

export const checkGraphSizeLimitExceeded = (
  nodes: EnrichedLineageNode[],
  renderedEdgeCount: number = 0,
) => {
  const parentNodes: EnrichedLineageNode[] = nodes.filter((n) => !n.parent);
  if (parentNodes.length >= MAX_GRAPH_NODES_WARNING) {
    debugLog('Graph size limit exceeded:', parentNodes.length, 'nodes');
    return true;
  }

  if (renderedEdgeCount >= MAX_GRAPH_EDGES_WARNING) {
    debugLog('Graph size limit exceeded:', renderedEdgeCount, 'edges');
    return true;
  }

  return false;
};

export const getLineageZoomTier = (zoom: number): LineageZoomTier => {
  if (zoom < LINEAGE_DOT_ZOOM_THRESHOLD) return 'dot';
  if (zoom < LINEAGE_PILL_ZOOM_THRESHOLD) return 'pill';
  return 'card';
};

export const selectLineageZoomTier = (state: ReactFlowState) =>
  getLineageZoomTier(state.transform[2]);

// In pill form an expanded node's descendants (columns, nested elements and
// their columns) stack as one continuous list of rows under the pill, instead
// of the card layout's headers, search field and per-child spacing.
const getCompactPanelNodes = (nodes: ReactFlowNode<ReactFlowNodeData>[]) => {
  const childrenByParentId = new Map<
    string,
    ReactFlowNode<ReactFlowNodeData>[]
  >();
  nodes.forEach((node) => {
    if (!node.parentNode || node.hidden) return;
    const siblings = childrenByParentId.get(node.parentNode) ?? [];
    siblings.push(node);
    childrenByParentId.set(node.parentNode, siblings);
  });
  if (childrenByParentId.size === 0) return nodes;

  const rowYByNodeId = new Map<string, number>();
  const panelBottomByNodeId = new Map<string, number>();
  const lastRowIds = new Set<string>();
  let lastPlacedRowId: string | undefined;

  const placeChildren = (parentId: string, startY: number): number => {
    let y = startY;
    [...(childrenByParentId.get(parentId) ?? [])]
      .sort((a, b) => a.position.y - b.position.y)
      .forEach((child) => {
        rowYByNodeId.set(child.id, y);
        lastPlacedRowId = child.id;
        y += placeChildren(child.id, GRAPH_CHILD_NODE_HEIGHT);
      });
    return y;
  };

  nodes.forEach((node) => {
    if (node.parentNode || !childrenByParentId.has(node.id)) return;
    lastPlacedRowId = undefined;
    panelBottomByNodeId.set(
      node.id,
      placeChildren(node.id, LINEAGE_PILL_BOTTOM),
    );
    if (lastPlacedRowId) lastRowIds.add(lastPlacedRowId);
  });

  return nodes.map((node) => {
    const panelBottom = panelBottomByNodeId.get(node.id);
    if (panelBottom !== undefined) {
      return {
        ...node,
        data: { ...node.data, compactPanelBottom: panelBottom },
      };
    }
    const rowY = rowYByNodeId.get(node.id);
    if (rowY === undefined) return node;
    return {
      ...node,
      position: { x: 0, y: rowY },
      data: lastRowIds.has(node.id)
        ? { ...node.data, isLastCompactRow: true }
        : node.data,
    };
  });
};

export const getLineageZoomTierGraph = (
  nodes: ReactFlowNode<ReactFlowNodeData>[],
  edges: ReactFlowEdge[],
  zoomTier: LineageZoomTier,
) => {
  if (zoomTier === 'card') return { nodes, edges };

  if (zoomTier === 'pill') {
    return { nodes: getCompactPanelNodes(nodes), edges };
  }

  const parentIdByNodeId = new Map<string, string>();
  nodes.forEach((node) => {
    if (node.parentNode) parentIdByNodeId.set(node.id, node.parentNode);
  });
  if (parentIdByNodeId.size === 0) return { nodes, edges };

  const getTopLevelNodeId = (nodeId: string): string => {
    const parentId = parentIdByNodeId.get(nodeId);
    return parentId ? getTopLevelNodeId(parentId) : nodeId;
  };

  const topLevelEdges = edges.filter(
    (edge) =>
      !parentIdByNodeId.has(edge.source) && !parentIdByNodeId.has(edge.target),
  );
  const visibleEdgeKeys = new Set(
    topLevelEdges
      .filter((edge) => !edge.hidden)
      .map((edge) => `${edge.source}-${edge.target}`),
  );

  // Column views hide parent edges that column edges already cover, so the
  // hidden columns' edges are redirected to their top-level nodes instead.
  const redirectedEdges: ReactFlowEdge[] = [];
  edges.forEach((edge) => {
    if (edge.hidden) return;
    if (
      !parentIdByNodeId.has(edge.source) &&
      !parentIdByNodeId.has(edge.target)
    ) {
      return;
    }

    const source = getTopLevelNodeId(edge.source);
    const target = getTopLevelNodeId(edge.target);
    const key = `${source}-${target}`;
    if (source === target || visibleEdgeKeys.has(key)) return;

    visibleEdgeKeys.add(key);
    redirectedEdges.push({
      ...edge,
      id: `dot-${key}`,
      source,
      target,
      style: {
        ...edgeStyle.parent,
        stroke: edge.style?.stroke ?? edgeStyle.parent.stroke,
      },
    });
  });

  return {
    nodes: nodes.map((node) =>
      parentIdByNodeId.has(node.id) ? { ...node, hidden: true } : node,
    ),
    edges: [...topLevelEdges, ...redirectedEdges],
  };
};

export const getLineageNodeDegrees = (
  edges: Pick<ReactFlowEdge, 'source' | 'target'>[],
): LineageNodeDegrees => {
  const byNodeId: Record<string, number> = {};
  let max = 0;
  edges.forEach(({ source, target }) => {
    byNodeId[source] = (byNodeId[source] ?? 0) + 1;
    byNodeId[target] = (byNodeId[target] ?? 0) + 1;
    max = Math.max(max, byNodeId[source], byNodeId[target]);
  });

  return { byNodeId, max };
};

export const areLineageNodeDegreesEqual = (
  a: LineageNodeDegrees,
  b: LineageNodeDegrees,
) => {
  if (a.max !== b.max) return false;
  const aNodeIds = Object.keys(a.byNodeId);
  return (
    aNodeIds.length === Object.keys(b.byNodeId).length &&
    aNodeIds.every((nodeId) => a.byNodeId[nodeId] === b.byNodeId[nodeId])
  );
};

export const getLineageDotDiameter = (degree: number, maxDegree: number) => {
  if (maxDegree <= 0) return LINEAGE_DOT_MIN_DIAMETER;
  // Log scale so a few highly connected hubs don't dwarf every other node.
  const ratio = Math.min(1, Math.log1p(degree) / Math.log1p(maxDegree));
  return (
    LINEAGE_DOT_MIN_DIAMETER +
    ratio * (LINEAGE_DOT_MAX_DIAMETER - LINEAGE_DOT_MIN_DIAMETER)
  );
};

export const getGraphNodeChildrenState = (data: ReactFlowNodeData) => {
  const showConnectedColumnsOnly = !!data.isColumnHighlightFiltered;

  // `columnsCount` only counts column descendants (0 when children are
  // elements), so prefer the loaded children count when present.
  const childrenCount = showConnectedColumnsOnly
    ? data.children?.length ?? 0
    : data.children?.length ||
      data.columnsCount ||
      data.metadata.columnsCount ||
      0;

  return {
    showConnectedColumnsOnly,
    childrenCount,
    isExpandable: !showConnectedColumnsOnly || childrenCount > 0,
    showChildrenSection:
      data.metadata.isRestricted !== true &&
      (showConnectedColumnsOnly ||
        childrenCount > 0 ||
        !!data.children ||
        !!data.isColumnsLoading),
    isVisuallyExpanded: !!data.isExpanded || !!data.isColumnsLoading,
  };
};

export const getGraphNodeBorderColor = (
  palette: Theme['palette'],
  {
    isHighlighted,
    isRestricted,
    colorByBorderColor,
  }: GraphNodeBorderColorParams,
) => {
  if (isHighlighted) return palette.accent.secondary;
  if (isRestricted) return palette.icon.tertiary;
  return colorByBorderColor;
};
